import React from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Stack,
    Typography
} from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import DevicesOtherRoundedIcon from '@mui/icons-material/DevicesOtherRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import { ILoginConflictSession, ILoginConflictState } from '../../Core/State/ILoginConflictState';
import { t } from '../../i18n';

interface DuplicateSessionLoginModalProps {
    conflict: ILoginConflictState | null;
    onConfirm: (conflictId: string) => void;
    onCancel: (conflictId: string) => void;
}

const BRAND_RED = '#F93751';
const BRAND_RED_HOVER = '#d12b3a';
const BRAND_AMBER = '#FFC857';
const SURFACE_CARD = '#1C1E26';
const SURFACE_CHIP = '#242632';
const TEXT_PRIMARY = '#FFFFFF';
const TEXT_SECONDARY = 'rgba(255,255,255,0.72)';
const TEXT_MUTED = 'rgba(255,255,255,0.50)';
const BORDER_SUBTLE = '1px solid rgba(255,255,255,0.10)';

const formatDateTime = (value?: string) => {
    if (!value) return 'Unknown';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString(undefined, {
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const getDeviceText = (session: ILoginConflictSession) => (
    session.deviceLabel || session.userAgent || t('Login:duplicateSession.unknownDevice')
);

const getIpText = (session: ILoginConflictSession) => {
    if (session.ipAddress && session.forwardedFor && session.forwardedFor !== session.ipAddress) {
        return `${session.ipAddress} (${session.forwardedFor})`;
    }
    return session.ipAddress || session.forwardedFor || t('Login:duplicateSession.unknown');
};

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography
        component="span"
        sx={{
            color: TEXT_MUTED,
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
        }}
    >
        {children}
    </Typography>
);

const InfoRow: React.FC<{ icon: React.ReactNode; value: string }> = ({ icon, value }) => (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
        <Box
            sx={{
                color: TEXT_MUTED,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                width: 16,
                height: 16,
            }}
        >
            {icon}
        </Box>
        <Typography
            noWrap
            sx={{
                color: TEXT_PRIMARY,
                fontSize: 12,
                fontWeight: 600,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: 1.4,
            }}
        >
            {value}
        </Typography>
    </Stack>
);

const SessionCard: React.FC<{
    session: ILoginConflictSession;
    index: number;
    showIndex: boolean;
}> = ({ session, index, showIndex }) => (
    <Box
        sx={{
            backgroundColor: SURFACE_CARD,
            border: BORDER_SUBTLE,
            borderRadius: '8px',
            p: 1.25,
        }}
    >
        {(showIndex || session.countryCode) && (
            <Stack
                direction="row"
                alignItems="center"
                spacing={0.85}
                sx={{ mb: 1, flexWrap: 'wrap', rowGap: 0.4 }}
            >
                {showIndex && (
                    <Typography
                        sx={{
                            color: TEXT_PRIMARY,
                            fontSize: 11,
                            fontWeight: 900,
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                            flex: '1 1 auto',
                            minWidth: 0,
                        }}
                    >
                        {t('Login:duplicateSession.sessionIndex', { number: index + 1 })}
                    </Typography>
                )}
                {session.countryCode && (
                    <Chip
                        label={session.countryCode.toUpperCase()}
                        size="small"
                        sx={{
                            height: 18,
                            color: TEXT_PRIMARY,
                            backgroundColor: SURFACE_CHIP,
                            border: BORDER_SUBTLE,
                            fontWeight: 800,
                            fontSize: 10,
                            letterSpacing: '0.06em',
                            '& .MuiChip-label': { px: 0.85 },
                        }}
                    />
                )}
            </Stack>
        )}
        <Stack spacing={0.7}>
            <InfoRow icon={<DevicesOtherRoundedIcon sx={{ fontSize: 14 }} />} value={getDeviceText(session)} />
            <InfoRow icon={<PublicRoundedIcon sx={{ fontSize: 14 }} />} value={getIpText(session)} />
            <InfoRow
                icon={<BoltRoundedIcon sx={{ fontSize: 14 }} />}
                value={t('Login:duplicateSession.activePrefix', { when: formatDateTime(session.lastActivityAt) })}
            />
        </Stack>
    </Box>
);

const DuplicateSessionLoginModal: React.FC<DuplicateSessionLoginModalProps> = ({
    conflict,
    onConfirm,
    onCancel
}) => {
    if (!conflict) return null;

    const isSubmitting = Boolean(conflict.isSubmitting);
    const confirming = conflict.submitAction === 'confirm';
    const cancelling = conflict.submitAction === 'cancel';
    const sessions = conflict.existingSessions || [];

    return (
        <Box
            role="alertdialog"
            aria-labelledby="duplicate-session-title"
            sx={{
                color: TEXT_PRIMARY,
                fontFamily: 'Inter, sans-serif',
                width: '100%',
            }}
        >
            <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1.75 }}>
                <Box
                    sx={{
                        alignItems: 'center',
                        backgroundColor: 'rgba(255,200,87,0.16)',
                        border: '1px solid rgba(255,200,87,0.45)',
                        borderRadius: '8px',
                        color: BRAND_AMBER,
                        display: 'flex',
                        flexShrink: 0,
                        height: 32,
                        justifyContent: 'center',
                        width: 32,
                    }}
                >
                    <WarningAmberRoundedIcon sx={{ fontSize: 18 }} />
                </Box>
                <Typography
                    id="duplicate-session-title"
                    sx={{
                        color: TEXT_PRIMARY,
                        fontSize: 16,
                        fontWeight: 900,
                        letterSpacing: '0.01em',
                        lineHeight: 1.2,
                    }}
                >
                    {t('Login:duplicateSession.title')}
                </Typography>
            </Stack>

            {conflict.user?.username && (
                <Stack
                    direction="row"
                    alignItems="baseline"
                    spacing={0.85}
                    sx={{ mb: 1.5, minWidth: 0 }}
                >
                    <SectionLabel>{t('Login:duplicateSession.account')}</SectionLabel>
                    <Typography
                        noWrap
                        sx={{
                            color: TEXT_PRIMARY,
                            fontSize: 14,
                            fontWeight: 900,
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {conflict.user.username}
                    </Typography>
                </Stack>
            )}

            {sessions.length > 0 ? (
                <Box sx={{ mb: 2 }}>
                    <Box sx={{ mb: 0.85 }}>
                        <SectionLabel>
                            {sessions.length > 1
                                ? t('Login:duplicateSession.existingSessionOther')
                                : t('Login:duplicateSession.existingSessionOne')}
                        </SectionLabel>
                    </Box>
                    <Stack spacing={1}>
                        {sessions.map((session, index) => (
                            <SessionCard
                                key={session.clientId || `${session.ipAddress || 'session'}-${index}`}
                                session={session}
                                index={index}
                                showIndex={sessions.length > 1}
                            />
                        ))}
                    </Stack>
                </Box>
            ) : (
                <Typography sx={{ color: TEXT_SECONDARY, fontSize: 12, mb: 2 }}>
                    {t('Login:duplicateSession.noSessionDetails')}
                </Typography>
            )}

            <Stack
                direction={{ xs: 'column-reverse', sm: 'row' }}
                spacing={1}
            >
                <Button
                    variant="outlined"
                    fullWidth
                    disabled={isSubmitting}
                    onClick={() => onCancel(conflict.conflictId)}
                    startIcon={cancelling ? <CircularProgress size={14} sx={{ color: TEXT_PRIMARY }} /> : undefined}
                    sx={{
                        borderColor: 'rgba(255,255,255,0.22)',
                        color: TEXT_PRIMARY,
                        fontWeight: 800,
                        letterSpacing: '0.01em',
                        py: 1.1,
                        textTransform: 'none',
                        '&:hover': {
                            borderColor: 'rgba(255,255,255,0.42)',
                            backgroundColor: 'rgba(255,255,255,0.04)',
                        },
                        '&.Mui-disabled': {
                            borderColor: 'rgba(255,255,255,0.12)',
                            color: 'rgba(255,255,255,0.35)',
                        },
                    }}
                >
                    {cancelling
                        ? t('Login:duplicateSession.buttons.keepingOther')
                        : t('Login:duplicateSession.buttons.keepOther')}
                </Button>
                <Button
                    variant="contained"
                    fullWidth
                    disabled={isSubmitting}
                    onClick={() => onConfirm(conflict.conflictId)}
                    startIcon={
                        confirming
                            ? <CircularProgress size={14} sx={{ color: TEXT_PRIMARY }} />
                            : <VerifiedUserRoundedIcon sx={{ fontSize: 16 }} />
                    }
                    sx={{
                        backgroundColor: BRAND_RED,
                        color: TEXT_PRIMARY,
                        fontWeight: 900,
                        letterSpacing: '0.01em',
                        py: 1.1,
                        textTransform: 'none',
                        boxShadow: '0 6px 16px rgba(249, 55, 81,0.32)',
                        '&:hover': {
                            backgroundColor: BRAND_RED_HOVER,
                            boxShadow: '0 6px 16px rgba(209,43,58,0.38)',
                        },
                        '&.Mui-disabled': {
                            backgroundColor: 'rgba(249, 55, 81,0.45)',
                            color: 'rgba(255,255,255,0.65)',
                        },
                    }}
                >
                    {confirming
                        ? t('Login:duplicateSession.buttons.loggingIn')
                        : t('Login:duplicateSession.buttons.loginHere')}
                </Button>
            </Stack>

        </Box>
    );
};

export default DuplicateSessionLoginModal;
